/* =========================================================
   MEZUZAH THEME-WIDE JS
   Counterpart to mezuzah-global.css. Loaded from theme.liquid
   on every page, deferred. Keep this file small: anything that
   belongs to one template belongs in that template's own asset.
   ========================================================= */

/* ---------------------------------------------------------
   JUDGE.ME HEADING DEMOTION SHIM (2026-07-21)
   Judge.me renders its write-review modal with real h1/h2/h3
   tags. Those tags are app-owned markup we cannot edit at
   source, and they inject themselves into the page's heading
   outline, so the first heading a crawler or outline tool sees
   on a PDP can be the modal's, not the product H1.

   This rewrites those tags to <p> after the app paints,
   copying every attribute across so Judge.me's own classes,
   ids and aria wiring (including any aria-labelledby target)
   keep resolving.

   Exact script as CTO-tested on the preview. Three things a
   future reader should know before touching it:

   1. The observer is deliberately broad, documentElement with
      subtree, because the modal is injected late and can be
      re-rendered at any time. It converges rather than looping:
      the second pass finds no h1/h2/h3 and mutates nothing. The
      cost is a querySelectorAll on every DOM mutation site-wide.
   2. Demoting a dialog heading to <p> removes its heading role,
      which is a real screen-reader navigation loss. The
      attribute copy keeps labelling intact, but a screen reader
      user can no longer jump to it as a heading. Accepted here
      as an outline-correctness tradeoff, not a free win.
   3. If Judge.me styles those nodes with bare tag selectors
      rather than classes, demotion changes their appearance.
      Verified on the PDP at implementation time.
   --------------------------------------------------------- */
(function () {
  function demote() {
    document.querySelectorAll('.jdgm-write-review-modal h1, .jdgm-write-review-modal h2, .jdgm-write-review-modal h3').forEach(function (h) {
      var p = document.createElement('p');
      for (var i = 0; i < h.attributes.length; i++) { p.setAttribute(h.attributes[i].name, h.attributes[i].value); }
      p.innerHTML = h.innerHTML;
      h.replaceWith(p);
    });
  }
  new MutationObserver(demote).observe(document.documentElement, { childList: true, subtree: true });
  document.addEventListener('DOMContentLoaded', demote);
})();

/* ---------------------------------------------------------
   CART QUANTITY RACE GUARD (ERRORS.md B10, 2026-07-21)
   Rapid clicks on the cart drawer quantity +/- buttons were
   being silently dropped: 3 fast clicks netted +1.

   Root cause, per the B10 investigation: each click calls
   input.stepUp() (correct, the DOM value moves immediately)
   and then dispatches a `change` with no debounce and no
   in-flight guard. Every change independently POSTs the input's
   CURRENT value to /cart/change as an ABSOLUTE quantity. Under
   rapid clicking several requests are in flight at once, each
   carrying a different snapshot, and response order is not
   guaranteed to match click order, so an earlier, lower
   snapshot can resolve last and win.

   The fix is a debounce, not a block. Blocking clicks while a
   request is in flight would also lose clicks, which is the
   very symptom. Here every click still increments the input,
   so the number stays responsive; only the network write is
   coalesced, and it carries the final value.

   Implemented as a capture-phase interceptor because the
   offending listener lives in bundled theme.js, which we do not
   edit. We swallow the app's change, then re-dispatch one after
   the clicks stop, flagged so we let our own through.
   --------------------------------------------------------- */
(function () {
  var DELAY = 350;
  var timers = new WeakMap();
  var reentry = new WeakSet();

  document.addEventListener('change', function (e) {
    var input = e.target;
    if (!input || input.tagName !== 'INPUT' || typeof input.closest !== 'function') return;
    if (!input.closest('quantity-input, .quantity')) return;
    // cart surfaces only: the PDP quantity picker writes nothing until add-to-cart
    if (!input.closest('.drawer, .cart, cart-drawer, form[action*="/cart"]')) return;

    if (reentry.has(input)) { reentry.delete(input); return; }

    e.stopImmediatePropagation();
    clearTimeout(timers.get(input));
    timers.set(input, setTimeout(function () {
      reentry.add(input);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }, DELAY));
  }, true);
})();

/* ---------------------------------------------------------
   CART FETCH RESILIENCE (WO-201, 2026-08-10; rewritten WO-342, 2026-09-07)
   Original bug (WO-201): when Cloudflare rate-limits a cart request, it
   answers with an HTML challenge page instead of JSON. theme.js's own
   cart code (bundled, not ours to edit) always does
   `.then(response => response.json())` with no content-type check,
   so JSON.parse throws on the HTML body and the customer sees a raw
   "Unexpected token '<'" in the buy column instead of any message.

   WO-342 rule: this wrapper's job is now strictly limited to two
   things, and nothing else --
   (1) if the fetch itself never comes back with a response at all
       (a real network failure -- TypeError, offline, DNS, etc.), retry
       ONCE, and only for /cart/add. Never for /cart/change or
       /cart/update, since both of those write an ABSOLUTE line
       quantity, not a delta -- replaying one after the customer has
       since changed the quantity again silently overwrites their
       later input with a stale one.
   (2) if a response IS received but its body isn't valid JSON (the
       original Cloudflare-challenge case above), show the friendly
       banner once, with NO retry.

   Every other received response -- 2XX, or any 4XX/5XX with a valid
   JSON body, which includes Shopify's own real business-rule errors
   (sold out, over the available quantity, quantity rule violations)
   -- is handed straight back to the caller untouched. theme.js's own
   per-endpoint handling already reads that body itself (addToCartError
   for /cart/add, the parsedState.errors check in updateCart for
   /cart/change) and shows Shopify's real message through the same
   `[data-cart-errors-container]` slot this wrapper's own friendly
   banner uses -- this file no longer intercepts that path or shows
   its own message for it.

   WO-342's own finding, not previously reported: the OLD version of
   this wrapper had no `.catch` at all, so a genuine network failure
   (fetch() itself rejecting) was never retried, while every RECEIVED
   response that wasn't `ok`, including a real, valid Shopify 422
   business error, WAS unconditionally replayed 3 seconds later with
   the exact same body. On /cart/add that replay could add the same
   line a second time (Shopify's own documented behavior: an overstock
   add adds up to the available maximum then returns 422, so the
   retry's second POST adds again); on /cart/change or /cart/update,
   replaying a stale absolute quantity 3 seconds later could silently
   overwrite whatever the customer had changed it to in the meantime.
   That is the mechanism behind the reported vanishing cart lines. This
   rewrite inverts both halves: never replay a response that arrived,
   retry (once, /cart/add only) only when no response arrived at all.

   Scope: matches the AJAX Cart API's three JSON write endpoints only.
   Checked live (window.theme.routes on the pushed preview) rather than
   assuming the commonly-documented Shopify ".js" suffix convention:
   this theme's cart_add_url/cart_change_url/cart_update_url are
   suffix-less (/cart/add, /cart/change, /cart/update), content-
   negotiated via an Accept: application/json header instead of the
   URL. Matches those three paths only -- NOT bare /cart (cart_url),
   used for real page navigation and for theme.js's own getCart() fetch
   (.../cart?section_id=..., consumed as text, not JSON, per
   theme.dev.js) -- and not /cart/clear, which no code in this theme
   calls (grepped assets/ and every .liquid file, zero matches; the
   original CART_JS_RE never matched it either).

   Message target unchanged from WO-201: theme.js's own
   `[data-cart-errors-container]` / `.errors` markup
   (snippets/product-buttons.liquid, upsell-product.liquid -- the buy
   column's native error slot, toggled via classList.add/remove
   ('is-visible') exactly as theme.js's own addToCartError does it,
   verified in theme.dev.js). Reusing it means no new CSS and no visual
   drift from the theme's existing error banner.
   --------------------------------------------------------- */
(function () {
  var CART_JS_RE = /\/cart\/(add|change|update)(\.js)?(\?|$)/;
  var CART_ADD_RE = /\/cart\/add(\.js)?(\?|$)/;
  var FRIENDLY_MESSAGE = 'One moment, the cart is busy. Please try again.';
  window.__mzCartBusyText = FRIENDLY_MESSAGE;   // WO-735 v6: the cart's existing busy text, shown by the discount recovery in place of a reload
  var nativeFetch = window.fetch.bind(window);

  function requestUrl(input) {
    if (typeof input === 'string') return input;
    if (input && typeof input.url === 'string') return input.url;
    return '';
  }

  function matchesCartPath(input, re) {
    try {
      return re.test(new URL(requestUrl(input), window.location.origin).pathname);
    } catch (e) {
      return false;
    }
  }

  function isCartJsonEndpoint(input) {
    return matchesCartPath(input, CART_JS_RE);
  }

  function isCartAddEndpoint(input) {
    return matchesCartPath(input, CART_ADD_RE);
  }

  // WO-735 v7: a rendered row owns its item key for its entire lifetime, including a detached row or a delayed quantity event.
  // Never resolve a positional write against a fresh server cart: its positions may already belong to different items.
  var lineAction = null;
  ['click', 'change', 'submit', 'theme:cart:update'].forEach(function (type) {
    document.addEventListener(type, function (e) {
      var row = e.target && e.target.closest && e.target.closest('[data-item]');
      var form = e.target && e.target.closest && e.target.closest('form[action*="/cart"]');
      if (!row && !form) return;
      var surface = form || row && row.closest('cart-drawer, [data-cart-page], cart-items') || document;
      var action = { key: row && row.getAttribute('data-item'), keys: renderedKeys(surface) };
      lineAction = action;
      Promise.resolve().then(function () { if (lineAction === action) lineAction = null; });
    }, true);
  });
  function renderedKeys(surface) {
    var keys = [];
    (surface || document).querySelectorAll('[data-item]').forEach(function (row) {
      var key = row.getAttribute('data-item');
      if (key && keys.indexOf(key) === -1) keys.push(key);
    });
    return keys;
  }
  function itemKey(id, keys) {
    var value = String(id);
    if (keys.indexOf(value) !== -1 || value.indexOf(':') !== -1 || !/^\d+$/.test(value)) return value;
    var matches = keys.filter(function (key) { return key.split(':')[0] === value; });
    return matches.length === 1 ? matches[0] : null;
  }
  function keyedCartBody(input, body, action, keys) {
    var change = matchesCartPath(input, /\/cart\/change(\.js)?$/);
    var params = body && typeof body.getAll === 'function' && typeof body.append === 'function';
    var data;
    if (params) {
      data = body instanceof FormData ? new FormData() : new URLSearchParams();
      body.forEach(function (v, k) { data.append(k, v); });
    } else if (typeof body === 'string') {
      try { data = JSON.parse(body); } catch (e) {
        if (body.indexOf('=') !== -1 && !/^\s*[\[{]/.test(body)) { params = true; data = new URLSearchParams(body); }
        else { if (change && body) throw new Error(FRIENDLY_MESSAGE); return { body: body, rows: isCartAddEndpoint(input) }; }
      }
    } else { return { body: body, rows: isCartAddEndpoint(input) }; }
    if (!data || typeof data !== 'object') return { body: body, rows: isCartAddEndpoint(input) };
    var rowFields = ['id', 'line', 'quantity', 'properties', 'selling_plan'];
    var rows = isCartAddEndpoint(input) || (change && rowFields.some(function (field) { return params ? data.has(field) : Object.prototype.hasOwnProperty.call(data, field); }));
    if (change && (rows || (params ? data.has('line') : Object.prototype.hasOwnProperty.call(data, 'line')))) {
      var line = params ? data.get('line') : data.line;
      var id = line != null ? action && action.key : itemKey(params ? data.get('id') : data.id, keys);
      if (!id || id === 'undefined' || id === 'null') throw new Error(FRIENDLY_MESSAGE);
      if (params) { data.delete('line'); data.set('id', id); }
      else { delete data.line; data.id = id; }
    }
    if (params) {
      var quantities = data.getAll('updates[]');
      var changes = [];
      data.forEach(function (v, k) { var m = /^updates\[([^\]]+)\]$/.exec(k); if (m) changes.push({ name: k, id: m[1], quantity: v }); });
      if (quantities.length) {
        rows = true;
        data.delete('updates[]');
        quantities.forEach(function (quantity, i) {
          var key = action && action.keys[i];
          if (!key) throw new Error(FRIENDLY_MESSAGE);
          data.append('updates[' + key + ']', quantity);
        });
      }
      changes.forEach(function (update) {
        var key = itemKey(update.id, keys);
        if (!key) throw new Error(FRIENDLY_MESSAGE);
        rows = true;
        data.delete(update.name);
        data.set('updates[' + key + ']', update.quantity);
      });
      if (data.has('discount')) rows = true;
    } else {
      if (Object.prototype.hasOwnProperty.call(data, 'updates')) {
        rows = true;
        var updates = {};
        Object.keys(data.updates || {}).forEach(function (id) {
          var key = Array.isArray(data.updates) ? action && action.keys[Number(id)] : itemKey(id, keys);
          if (!key) throw new Error(FRIENDLY_MESSAGE);
          updates[key] = data.updates[id];
        });
        data.updates = updates;
      }
      if (Object.prototype.hasOwnProperty.call(data, 'discount')) rows = true;
    }
    return { body: params ? data : JSON.stringify(data), rows: rows };
  }

  function hasValidJsonBody(response) {
    return response
      .clone()
      .text()
      .then(
        function (text) {
          try {
            JSON.parse(text);
            return true;
          } catch (e) {
            return false;
          }
        },
        function () {
          return false;
        }
      );
  }

  function toggleFriendlyMessage(show) {
    document.querySelectorAll('[data-cart-errors-container]').forEach(function (el) {
      if (show) el.innerHTML = '<div class="errors">' + FRIENDLY_MESSAGE + '</div>';
      el.classList.toggle('is-visible', show);
    });
  }

  // WO-735 (Codex re-review): the discount code waits for Broadcast's cart rows refresh (getCart, section api-cart-items) to answer before the controls come back
  var rowsWaiters = [];
  function isCartRowsRequest(input) {
    var u = typeof input === 'string' ? input : (input && input.url) || '';
    return u.indexOf('section_id=api-cart-items') !== -1;
  }
  window.__mzNextCartRows = function () { return new Promise(function (res) { rowsWaiters.push(res); }); };

  window.fetch = function (input, init) {
    if (isCartRowsRequest(input)) {
      // The recovery owns only its read, not any other refresh. Its read is excluded from the quiet checks before its build.
      if (init && init.__mzRecoveryRows) return nativeFetch(input, init);
      var waiters = rowsWaiters.splice(0);
      var owner = init && init.__mzRowsOwner;
      if (owner) owner.inflight = true;
      // WO-735 v2 (Codex): release only after this answer has been read and Broadcast's rebuild (which runs in the same chain right after its own read) had its turn, or the request failed
      window.__mzRowsInflight = (window.__mzRowsInflight || 0) + 1;   // WO-735 v6b: a mutation is not acknowledged until its rows refresh is read
      var controller = new AbortController();
      var signal = init && init.signal || input && input.signal;
      var abort = function () { controller.abort(); };
      if (signal) { if (signal.aborted) abort(); else signal.addEventListener('abort', abort, { once: true }); }
      var started = Date.now();
      var timeout;
      var released = false;
      var release = function () {
        if (released) return;
        released = true;
        clearTimeout(timeout);
        if (signal) signal.removeEventListener('abort', abort);
        setTimeout(function () { if (owner) owner.inflight = false; window.__mzRowsInflight = Math.max(0, (window.__mzRowsInflight || 1) - 1); waiters.forEach(function (w) { w(); }); }, 50);
      };
      // A read that never answers cannot own controls forever. Cancel it before releasing its counter, so its late answer cannot rebuild stale rows.
      (function look() {
        if (Date.now() - started >= 10000) { if (owner) owner.cancelled = true; abort(); release(); return; }
        timeout = setTimeout(look, 150);
      })();
      var next = Object.assign({}, init || {}, { signal: controller.signal });
      return nativeFetch(input, next).then(function (r) {
        r.clone().text().then(release, release);
        return r;
      }, function (e) { release(); throw e; });
    }
    if (!isCartJsonEndpoint(input)) return nativeFetch(input, init);
    var keys = renderedKeys();
    var action = lineAction;
    var method = String(init && init.method || input && input.method || 'GET').toUpperCase();
    if (input && typeof input !== 'string' && typeof input.clone === 'function' && method !== 'GET' && method !== 'HEAD' && !(init && Object.prototype.hasOwnProperty.call(init, 'body'))) {
      var contentType = input.headers && input.headers.get('Content-Type') || '';
      var read = /multipart\/form-data/i.test(contentType) ? input.clone().formData() : input.clone().text();
      return read.then(function (body) {
        var next = Object.assign({}, init || {}, { body: body });
        if (body instanceof FormData) { next.headers = new Headers(init && init.headers || input.headers); next.headers.delete('Content-Type'); }
        return cartWrite(input, next, action, keys);
      });
    }
    return cartWrite(input, init, action, keys);
  };
  function cartWrite(input, init, action, keys) {
    var request;
    try {
      var url = new URL(requestUrl(input), window.location.origin);
      var query = !(init && init.body) && (['id', 'line', 'quantity', 'selling_plan', 'properties', 'discount'].some(function (key) { return url.searchParams.has(key); }) || Array.from(url.searchParams.keys()).some(function (key) { return /^(updates|properties)\[/.test(key); }));
      request = keyedCartBody(input, query ? url.searchParams : init && init.body, action, keys);
      if (query) {
        url.search = request.body.toString();
        input = typeof input === 'string' ? url.href : new Request(url.href, input);
      }
    }
    catch (e) { return Promise.reject(e); }
    if (!query && init && Object.prototype.hasOwnProperty.call(init, 'body')) {
      init = Object.assign({}, init, { body: request.body });
      if (request.body instanceof FormData) { init.headers = new Headers(init.headers || input && input.headers); init.headers.delete('Content-Type'); }
    }
    var gen;
    if (request.rows) {
      window.__mzCartGen = (window.__mzCartGen || 0) + 1;
      gen = window.__mzCartGen;
      window.__mzCartInflight = (window.__mzCartInflight || 0) + 1;
      toggleFriendlyMessage(false);
    }
    var mzAck = function () { if (request.rows) window.__mzCartInflight = Math.max(0, (window.__mzCartInflight || 1) - 1); };

    // WO-730 P1.6 (Codex, CTO ruling): a cart request is NEVER sent a second time by this wrapper. A network failure can mean the server accepted the add and only the answer was lost, or is
    // still processing it; an equal cart snapshot does not prove otherwise (the retry and the original could both commit). The failure goes to the caller, which shows its existing error, and
    // the customer taps again. (WO-711 and WO-726 had a snapshot guarded retry: removed, it also cost up to 400 ms before every Add to Cart.)
    var mzReq = nativeFetch(input, init)
      .then(function (response) {
        // A response was received (first try, or the one network-failure
        // retry above) -- never replay again from here, whatever its
        // status. Only surface our own message when the body isn't valid
        // JSON at all; anything else goes straight back to the caller so
        // the caller's own error handling shows Shopify's real message.
        return hasValidJsonBody(response).then(function (valid) {
          if (!valid && request.rows && (window.__mzCartGen || 0) === gen) toggleFriendlyMessage(true);
          return response;
        });
      });
    return mzReq.then(function (response) { mzAck(); return response; }, function (error) { mzAck(); throw error; });
  }
})();

/* Mobile filter groups: native default_open is global (no breakpoint
   split) so the mobile drawer opens every group. Collapse them <=749px
   while desktop stays open. Groups remain tappable via data-collapsible.
   ISS pre-flip sweep 2026-07-25. */
(function () {
  var mq = window.matchMedia('(max-width: 749px)');
  function collapse() {
    if (!mq.matches) return;
    var groups = document.querySelectorAll('.collection__sidebar__slider details[data-collapsible][open]');
    groups.forEach(function (d) {
      d.removeAttribute('open');
      var body = d.querySelector('[data-collapsible-body]');
      if (body) { body.style.height = '0px'; }
    });
  }
  if (document.readyState !== 'loading') collapse();
  else document.addEventListener('DOMContentLoaded', collapse);
  window.addEventListener('load', collapse);
})();

/* ---------------------------------------------------------
   SCROLL-LOCK WATCHDOG (WO-213 P1, 2026-08-12)
   Owner-reported: landing on a PDP at 375px can leave the viewport
   stuck on the photo area -- vertical swipes do nothing. Reproduced
   live on the pushed V6 preview by racing the drawer's own open/close
   transition, not by anything gallery-specific:

   1. Open the mobile drawer (tap the hamburger).
   2. Tap .drawer__close before the open transition settles (<~150ms
      after open). The close click is swallowed -- the drawer keeps
      its is-open class, never actually closes -- AND
      document.documentElement keeps data-scroll-locked forever.
      Confirmed via direct event instrumentation: theme:scroll:lock
      fires, theme:scroll:unlock never does. Waiting ~600ms before
      closing (letting the transition finish) closes correctly and
      fires the matching unlock every time.
   3. Ruled out first, so this isn't guesswork: touch-action and
      overscroll-behavior stay "auto" throughout (never change), and
      Event.prototype.preventDefault was patched globally during
      testing and never fired for any touchmove -- the failure is
      entirely the stuck data-scroll-locked attribute, not a CSS or
      JS touch-blocking mechanism.

   theme.js (bundled Broadcast core, not ours to edit) owns the
   drawer/popup/modal open+close wiring and the scroll-lock library
   itself, so the actual race can't be patched at its source from
   here. Two things worth recording about the fix that DIDN'T work,
   since the wrong one is tempting: dispatching 'theme:scroll:unlock'
   directly (the theme's own public event, matches what a correct
   close does) DOES clear data-scroll-locked and the underlying
   library's own state -- but the swallowed close click also never
   removed the drawer's is-open class or its overlay, and that overlay
   (position:fixed, full-viewport, still layered above the page) goes
   on physically intercepting touch input regardless of whether
   scroll is "unlocked" underneath it. Unlocking the flag without
   also closing the drawer fixes nothing a user can feel.

   What actually works, verified live: after the delay, if the page
   is still marked locked, RE-DISPATCH A CLICK on the SAME close
   button the user already tapped. By then the transition window that
   swallowed the first click has passed, so the retry goes through
   theme.js's own normal close handling and everything -- the is-open
   class, the overlay, the scroll lock -- clears together, correctly,
   through the real code path instead of a hand-patched approximation
   of it.
   --------------------------------------------------------- */
(function () {
  var CHECK_DELAY = 500; // longer than the ~150-600ms window the race lives in
  var POLL_INTERVAL = 1000;

  // WO-654 F19: the desktop header search overlay also holds the scroll lock (SearchPopdown.open() locks, and marks
  // header-search-popdown.is-open), so it counts as open here. The drawer's own search popdown inside mobile-menu is not
  // an overlay and does not count (the mobile menu search guard at the end of this file clears it).
  function searchOverlayOpen() {
    var pops = document.querySelectorAll('header-search-popdown.is-open');
    for (var i = 0; i < pops.length; i++) {
      if (!pops[i].closest('mobile-menu')) return true;
    }
    return false;
  }

  function anythingActuallyOpen() {
    return !!(
      document.querySelector('.drawer.is-open, header-drawer.is-open') ||
      document.querySelector('dialog[open]') ||
      document.querySelector('popup-component .is-open, .popup.is-open') ||
      searchOverlayOpen()
    );
  }

  // Retries the user's own close click through the theme's real close
  // handler once the swallow window has passed, rather than
  // hand-patching the lock flag (see comment above -- that clears the
  // flag but leaves the overlay physically blocking touch).
  document.addEventListener('click', function (e) {
    if (!e.target.closest) return;
    var closer = e.target.closest('.drawer__close, [data-close-drawer], [data-popup-close], .product-modal__close');
    if (!closer) return;
    // WO-654 F19: re-click only when the close was really swallowed, i.e. the open drawer or popup holding this button
    // never lost is-open during the wait. If it closed and was opened again inside the 500 ms (tap X, tap the menu
    // straight away), the retry used to close the new menu by itself.
    var holder = closer.closest('.is-open');
    var closedInWait = false;
    var watcher = null;
    if (holder && window.MutationObserver) {
      watcher = new MutationObserver(function (records) {
        records.forEach(function (r) {
          if (r.oldValue && /(^|\s)is-open(\s|$)/.test(r.oldValue) && !r.target.classList.contains('is-open')) closedInWait = true;
        });
      });
      watcher.observe(holder, { attributes: true, attributeFilter: ['class'], attributeOldValue: true });
    }
    setTimeout(function () {
      if (watcher) watcher.disconnect();
      if (closedInWait) return;
      if (document.documentElement.hasAttribute('data-scroll-locked')) closer.click();
    }, CHECK_DELAY);
  }, true);

  // Backstop for paths with no close-button click to hang a retry off
  // (Escape key, overlay tap, programmatic close): no specific button
  // to re-click here, so fall back to the direct unlock event. Gated
  // on nothing being genuinely open, so it only fires for the "flag
  // stuck but the close otherwise worked" shape of this bug, not the
  // "close was swallowed entirely" shape the retry above handles.
  setInterval(function () {
    if (document.documentElement.hasAttribute('data-scroll-locked') && !anythingActuallyOpen()) {
      document.dispatchEvent(new CustomEvent('theme:scroll:unlock', { bubbles: true }));
    }
  }, POLL_INTERVAL);

  // bfcache restore (back/forward nav) can resurrect a stale locked
  // attribute from before the page was cached.
  window.addEventListener('pageshow', function () {
    if (document.documentElement.hasAttribute('data-scroll-locked') && !anythingActuallyOpen()) {
      document.dispatchEvent(new CustomEvent('theme:scroll:unlock', { bubbles: true }));
    }
  });
})();

/* ---------------------------------------------------------
   THIRD-PARTY SCROLL-LOCK CLEANUP (WO-232, 2026-08-12)
   klaviyo-prevent-body-scrolling is a class Klaviyo's own onsite
   popup script adds to <body> when it shows a popup, backed by a
   real overflow:hidden rule from Klaviyo's own injected
   stylesheet. It is not ours: confirmed by exhaustive grep, the
   class does not appear anywhere in this repo, in theme.js, or in
   vendor.js. Klaviyo's own close button does not reliably remove
   it -- reproduced live, closing a real popup left the class in
   place, and Klaviyo's own localStorage.klaviyoOnsite bookkeeping
   (disabledForms vs viewedForms) showed the form was never marked
   dismissed on their side either. That is a defect in their
   script, not ours, and it is not our place to patch a vendor's
   script. Full evidence: CTO-INBOX.md, WO-232.

   Our own drawer/cart/search scroll-lock (theme.js + vendor.js's
   bundled scroll-lock library) was independently verified correct
   in isolation: it locks and restores body.style.overflow cleanly
   every time, with no Klaviyo popup involved. The bug a shopper
   actually hits is that OUR drawer's unlock event fires and clears
   OUR lock, while KLAVIYO's separate, independent lock silently
   outlives it, and the page reads as permanently frozen even
   though our own code did everything right.

   Fix is defensive hardening of our own unlock path only, nothing
   Klaviyo-specific patched or touched: every time our theme
   unlocks scroll, force body.style.overflow back to '' (harmless
   if nothing else was holding it), then strip Klaviyo's class --
   but ONLY when no Klaviyo modal is actually visible right now.
   Klaviyo marks its own popup wrapper with
   data-kl-scroll-locking-modal="true" (confirmed live: the actual
   dialog element carries role="dialog" aria-modal="true" plus that
   attribute). If that's present with real dimensions, a Klaviyo
   popup is genuinely showing, and stripping the class would let
   the page scroll behind a live modal -- worse than the frozen
   page it replaces. Do nothing in that case and leave the class
   alone; Klaviyo's own close (or the watchdog above, if the whole
   drawer is stuck) can still clear things afterward.

   The setTimeout(...,0) below is deliberate, not decoration: it
   runs our cleanup after theme.js's own same-tick unlock handler
   (the vendor.js scroll-lock library's own restore-from-saved-
   value logic) has already finished, so our forced '' is always
   the last word regardless of listener registration order.
   --------------------------------------------------------- */
(function () {
  var KLAVIYO_LOCK_CLASS = 'klaviyo-prevent-body-scrolling';

  function klaviyoModalVisible() {
    var modal = document.querySelector('[data-kl-scroll-locking-modal="true"]');
    if (!modal) return false;
    var style = getComputedStyle(modal);
    if (style.display === 'none' || style.visibility === 'hidden') return false;
    var rect = modal.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  }

  document.addEventListener('theme:scroll:unlock', function () {
    setTimeout(function () {
      // WO-709 A6: no unconditional document.body.style.overflow = '' here (it could undo a lock another owner still holds: a mobile jump); the scroll-lock owner restores overflow.
      if (!klaviyoModalVisible()) {
        document.body.classList.remove(KLAVIYO_LOCK_CLASS);
      }
    }, 0);
  });
})();

/* ---------------------------------------------------------
   MOBILE MENU SEARCH GUARD (WO-654 F01, 2026-09-30)
   Shay's bug (shay-22 to shay-24): open the mobile menu after a second page view and every row is blank.
   Cause: the drawer search sits in <details open> (sections/mobile-menu.liquid), which fires a toggle event
   once when the page parses. theme.js's header-search-popdown answers that event with open(): it adds is-open
   and dispatches theme:scroll:lock. When theme.js is already cached and running by then (the second page view)
   the popdown is left "open" behind the drawer: header-dropdown.css hides the rows and the page scroll stays
   locked. The drawer search is never an overlay, so this removes is-open from that one popdown whenever it
   appears and, if nothing else is open, releases the lock. The class removal is safe: .mobile-menu
   .search-popdown is forced to opacity 1 (mezuzah-global.css), so no opacity transition runs and theme.js's
   transitionend handler never closes the <details>. The desktop header search is a different popdown (not
   inside mobile-menu) and is untouched.
   --------------------------------------------------------- */
(function () {
  var watched = null;

  // any drawer, dialog or popup that is genuinely open owns the scroll lock (same list the watchdog above uses)
  function drawerOpen() {
    return !!document.querySelector('.drawer.is-open, header-drawer.is-open, dialog[open], popup-component .is-open, .popup.is-open');
  }

  function release() {
    var pop = document.querySelector('mobile-menu header-search-popdown');
    if (!pop || !pop.classList.contains('is-open')) return;
    pop.classList.remove('is-open');
    unlockIfIdle();
    // the lock event that came with the class can land a moment later than the class itself; look once more
    setTimeout(unlockIfIdle, 400);
  }

  function unlockIfIdle() {
    var pop = document.querySelector('mobile-menu header-search-popdown');
    if (drawerOpen() || (pop && pop.classList.contains('is-open'))) return;
    if (document.documentElement.hasAttribute('data-scroll-locked')) {
      document.dispatchEvent(new CustomEvent('theme:scroll:unlock', { bubbles: true }));
    }
  }

  function watch() {
    var pop = document.querySelector('mobile-menu header-search-popdown');
    if (!pop || pop === watched) return;
    watched = pop;
    new MutationObserver(release).observe(pop, { attributes: true, attributeFilter: ['class'] });
    release();
  }

  if (document.readyState !== 'loading') watch();
  else document.addEventListener('DOMContentLoaded', watch);
  window.addEventListener('pageshow', watch);
})();

/* ---------------------------------------------------------
   CART DRAWER GIFT BOX IN VIEW (WO-660 item 4, 2026-09-30)
   Ticking "This order is a gift" in the cart drawer opens the message box (mezuzah-global.css .mez-gift-widget.is-open). When the opened box sits
   below the visible part of the drawer body, scroll the body (smoothly) just far enough to show all of it; nothing happens if it is already in view.
   Delegated on the document so it survives the drawer being re-rendered after a cart change. Drawer only.
   --------------------------------------------------------- */
(function () {
  document.addEventListener('change', function (e) {
    var input = e.target;
    if (!input || input.type !== 'checkbox' || !input.closest || !input.closest('.drawer--cart .cart__gift-check')) return;
    if (!input.checked) return;
    setTimeout(function () {
      var body = input.closest('.drawer__body');
      var widget = input.closest('.mez-gift-widget') || input.closest('.cart__gift-card');
      var box = widget && (widget.querySelector('.cart__field--textarea') || widget);
      if (!body || !box || !box.offsetHeight) return;
      var b = body.getBoundingClientRect();
      var r = box.getBoundingClientRect();
      var over = r.bottom - b.bottom + 16;
      if (over > 0) body.scrollBy({ top: over, behavior: 'smooth' });
    }, 350);
  });
})();

/* ---------------------------------------------------------
   WO-711 BROADCAST OVERRIDES (Codex findings 17, 18, 19). assets/theme.js is Broadcast core and is NOT edited; these replace three methods on its
   custom elements after they are defined. Each one keeps the original behavior and fixes the defect named below.
   17: updateCartDiscounts() disables every cart control and, when the discount request fails, never enabled them again (the customer could not edit the cart or
       check out until a reload). The override returns the whole operation and enables the controls on any failure.
   18: bindDiscountEventListeners() removed a handler that was never the one it added, so every rebuild stacked one more remove-discount listener. The override keeps
       one handler per button.
   19: cart-drawer trapFocus callbacks (animation end, the wallet buttons observer) could run after the drawer closed and re-trap focus. They are skipped unless the
       drawer is open, and the wallet observer is disconnected when the drawer closes.
   --------------------------------------------------------- */
(function () {
  function patchCartItems(C) {
    var proto = C.prototype;
    if (proto.__mzDiscountPatched) return;
    proto.__mzDiscountPatched = true;
    var handlers = new WeakMap();
    var quantityHandlers = new WeakSet();
    function bindKeyedQuantities() {
      document.querySelectorAll('[data-item] quantity-input input').forEach(function (input) {
        if (quantityHandlers.has(input)) return;
        quantityHandlers.add(input);
        input.addEventListener('change', function () {
          if (!/^updates\[[^\]]+\]$/.test(input.name)) return;
          var quantity = input.closest('quantity-input');
          if (quantity && typeof quantity.updateCart === 'function') quantity.updateCart();
        });
      });
    }
    var originalCartUpdateEvents = proto.cartUpdateEvents;
    proto.cartUpdateEvents = function () {
      var result = typeof originalCartUpdateEvents === 'function' && originalCartUpdateEvents.apply(this, arguments);
      bindKeyedQuantities();
      return result;
    };
    proto.getCartItemCount = function () {
      return Array.from(this.cart.querySelectorAll('input[name^="updates["]')).reduce(function (total, input) {
        return /^updates\[[^\]]*\]$/.test(input.name) ? total + parseInt(input.value) : total;
      }, 0);
    };
    proto.resetLineItem = function (item) {
      var input = item && item.querySelector('input[name^="updates["]');
      if (input) input.value = input.getAttribute('value');
      if (item) item.classList.remove('is-loading');
    };
    var originalAddToCart = proto.addToCart;
    var originalAddToCartError = proto.addToCartError;
    var originalEnableCartButtons = proto.enableCartButtons;
    if (typeof originalAddToCart === 'function' && typeof originalAddToCartError === 'function') {
      var addOwners = new WeakMap();
      proto.addToCart = function (formData, button) {
        var owner = {};
        if (button) addOwners.set(button, owner);
        this.__mzLastAddOwner = owner;
        try { return originalAddToCart.apply(this, arguments); }
        finally { owner.gen = window.__mzCartGen || 0; }
      };
      function ownsAddFailure(owner) {
        return !owner || (window.__mzCartGen || 0) === owner.gen && mzIsQuiet();
      }
      function settleAddFailure(self, owner) {
        if (!self.cart || !owner || (window.__mzCartGen || 0) !== owner.gen) return;
        if (!owner.settling) owner.settling = mzSettleControls(self, 1);
        return owner.settling;
      }
      proto.addToCartError = function (data, button) {
        var self = this;
        var owner = button && addOwners.get(button) || this.__mzLastAddOwner;
        this.__mzAddErrorOwner = owner;
        Promise.resolve().then(function () { if (self.__mzAddErrorOwner === owner) delete self.__mzAddErrorOwner; });
        var classes = this.cart && this.cart.classList;
        var ownRemove = classes && Object.prototype.hasOwnProperty.call(classes, 'remove');
        var remove = classes && classes.remove;
        // Native Add errors still update their product UI; their loading removal and catch unlock belong only to this Add owner.
        if (classes) classes.remove = function () {
          var names = Array.prototype.slice.call(arguments);
          if (!ownsAddFailure(owner)) names = names.filter(function (name) { return name !== 'is-loading'; });
          return remove.apply(classes, names);
        };
        try { return originalAddToCartError.apply(this, arguments); }
        finally {
          if (classes) { if (ownRemove) classes.remove = remove; else delete classes.remove; }
          if (!self.showCannotAddMoreInCart) settleAddFailure(self, owner);
        }
      };
      proto.enableCartButtons = function () {
        var owner = this.__mzAddErrorOwner;
        if (!ownsAddFailure(owner)) {
          return settleAddFailure(this, owner);
        }
        return originalEnableCartButtons.apply(this, arguments);
      };
    }
    var originalGetCart = proto.getCart;
    // Each rows answer belongs to the row action that started it. A late answer must never rebuild or unlock over a newer row request.
    proto.getCart = function () {
      var self = this;
      if (!window.theme.routes.cart_url) return originalGetCart.apply(this, arguments);
      var gen = window.__mzCartGen || 0;
      var unacknowledged = window.__mzCartInflight > 0;
      var ownRows = { inflight: false };
      function recover() {
        if ((window.__mzCartGen || 0) !== gen || !self.cart.classList.contains('is-loading')) return;
        return mzRecoveryRows(self, gen).then(function () {
          if ((window.__mzCartGen || 0) === gen) return mzSettleControls(self, 2);
        });
      }
      fetch(window.theme.routes.cart_url + '?section_id=api-cart-items', { __mzRowsOwner: ownRows })
        .then(function (r) {
          if (typeof self.cartErrorsHandler === 'function') return self.cartErrorsHandler(r);
          if (!r.ok) throw r;
          return r;
        })
        .then(function (r) { return r.text(); })
        .then(function (text) {
          if (ownRows.cancelled || (window.__mzCartGen || 0) !== gen) return;
          window.PresidioPlus = window.PresidioPlus || {};
          if ((window.GWP_CART_DRAWER_CONFIG || window.BUY_X_GET_Y_CART_DRAWER_CONFIG) && window.PresidioPlus.gwpProcessed === false) return;
          return mzQuiet(ownRows).then(function (quiet) {
            if (!quiet || ownRows.cancelled || (window.__mzCartGen || 0) !== gen || !mzIsQuiet(ownRows)) return;
            if (unacknowledged) { self.getCart(); return; }
            var d = document.createElement('div');
            d.innerHTML = text;
            var content = d.querySelector('[data-api-content]');
            if (!content) return recover();
            if (content && (window.__mzCartGen || 0) === gen && mzIsQuiet(ownRows)) self.build(content);
          });
        })
        .catch(function (error) {
          console.log(error);
          return recover();
        });
    };
    // Broadcast already passes the original key with each remove and quantity event. Keep it all the way to Shopify instead of converting it to a line number.
    proto.updateCart = function (updateData, currentItem) {
      var self = this;
      updateData = updateData || {};
      var key = currentItem && currentItem.getAttribute('data-item') || updateData.id;
      if (!key) return Promise.resolve(false);
      var item = currentItem || Array.prototype.find.call(this.cart.querySelectorAll('[data-item]'), function (row) { return row.getAttribute('data-item') === String(key); });
      var itemTitle = item && item.getAttribute('data-item-title');
      window.PresidioPlus = window.PresidioPlus || {};
      window.PresidioPlus.gwpProcessed = false;
      this.cart.classList.add('is-loading');
      if (item) item.classList.add(updateData.quantity ? 'is-loading' : 'is-removed');
      this.disableCartButtons();
      var request = fetch(window.theme.routes.cart_change_url, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: String(key), quantity: updateData.quantity })
      });
      var gen = window.__mzCartGen || 0;
      return request.then(function (r) { return r.text(); }).then(function (text) {
        var state = JSON.parse(text);
        if (state.errors) {
          if ((window.__mzCartGen || 0) !== gen) return false;
          self.cartUpdateFailed = true;
          self.updateErrorText(itemTitle);
          self.toggleErrorMessage();
          if (item) self.resetLineItem(item);
          self.bindDiscountEventListeners();
          return mzQuiet().then(function (quiet) { if (quiet && (window.__mzCartGen || 0) === gen && mzIsQuiet()) self.enableCartButtons(); return false; });
        }
        if ((window.__mzCartGen || 0) !== gen) return false;
        if (!window.GWP_CART_DRAWER_CONFIG && !window.BUY_X_GET_Y_CART_DRAWER_CONFIG) self.getCart();
        else document.dispatchEvent(new CustomEvent('theme:cart:change', { bubbles: true, detail: { source: 'theme-update-cart' } }));
        return true;
      }).catch(function (error) {
        console.log(error);
        if ((window.__mzCartGen || 0) !== gen) return false;
        return mzSettleControls(self, 0).then(function () { return false; });
      });
    };
    proto.bindDiscountEventListeners = function () {
      if (!this.hasDiscountBlock) return;
      var self = this;
      this.discounts = document.querySelectorAll('[data-discount-body]');
      this.discounts.forEach(function (body) {
        var code = body.dataset.discountCode;
        // WO-719 BUY-05: the code keeps the characters it was entered with (snippets/cart-price.liquid no longer handleizes it); codes are compared without case
        var lc = String(code).toLowerCase();
        if (!self.existingDiscountCodes.some(function (c) { return String(c).toLowerCase() === lc; })) self.existingDiscountCodes.push(code);
        var btn = body.querySelector('[data-remove-discount]');
        if (!btn) return;
        var old = handlers.get(btn);
        if (old) btn.removeEventListener('click', old);
        var h = function (e) { e.preventDefault(); self.removeDiscount(code); };
        handlers.set(btn, h);
        btn.addEventListener('click', h);
      });
    };
    // WO-726 P2-1 (LAW 51): Broadcast's removeDiscount compares codes exactly, but a customer may have typed the code in another case than the merchant defined it (BUY-05 keeps the code as defined);
    // codes are matched without case here.
    proto.removeDiscount = function (code) {
      var lc = String(code).toLowerCase();
      var has = this.existingDiscountCodes.some(function (c) { return String(c).toLowerCase() === lc; });
      if (!has) return;
      // WO-730 P2.2 (Codex): the code leaves the local list only when the POST succeeded; a failed removal is restored, so the next click on its X sends the request again
      var previous = this.existingDiscountCodes.slice();
      var self = this;
      this.existingDiscountCodes = this.existingDiscountCodes.filter(function (c) { return String(c).toLowerCase() !== lc; });
      var request = this.updateCartDiscounts(this.existingDiscountCodes.join(','));
      var gen = window.__mzCartGen || 0;
      return Promise.resolve(request).then(function (ok) {
        if (ok === false && (window.__mzCartGen || 0) === gen) self.existingDiscountCodes = previous;
      });
    };
    // WO-735: applying a code while a discount operation is open would leave the new code in Broadcast's local list although nothing was sent for it: the click is ignored before the list is touched
    var origApply = proto.applyDiscount;
    if (typeof origApply === 'function') {
      proto.applyDiscount = function () {
        if (this.__mzDiscountBusy) return;
        return origApply.apply(this, arguments);
      };
    }
    // WO-735 v5 (Codex P1-A, P1-B): the controls come back only on fresh rows. A newer cart action that owns the loading state keeps them off until ITS rows refresh answers;
    // if that refresh failed (Broadcast leaves is-loading set), the rows are read once more (GET only).
    // WO-735 v7: every step takes the cart rows counter (window.__mzCartGen, bumped only by adds, row changes, quantity updates and discounts) and does NOTHING if a newer cart
    // action started during the step: that action owns the controls; the recovery read is made here (not through getCart) so its answer is built only while no newer action has started; (2) no reload
    // (it could throw away a gift draft whose save failed): when the cart is still loading after the two waits, the loading state is cleared, the controls come back and the cart's existing busy text shows.
    // WO-735 v6b (Codex V36 verdict): a cart change that is unacknowledged (its request or its rows refresh still open) owns the controls. The recovery read is neither started nor applied while one is open
    // (it would rebuild rows older than that change and unlock them); it waits up to 10 s for quiet and then does nothing.
    function mzIsQuiet(ownRows) {
      return !(window.__mzCartInflight > 0) && !((window.__mzRowsInflight || 0) - (ownRows && ownRows.inflight ? 1 : 0) > 0);
    }
    function mzQuiet(ownRows) {
      return new Promise(function (res) {
        var t0 = Date.now();
        (function look() {
          if (mzIsQuiet(ownRows)) return res(true);
          if (Date.now() - t0 > 10000) return res(false);
          setTimeout(look, 150);
        })();
      });
    }
    function mzRecoveryRows(self, gen) {
      return mzQuiet().then(function (quiet) {
        if (!quiet || (window.__mzCartGen || 0) !== gen || window.__mzCartInflight > 0 || window.__mzRowsInflight > 0) return;
        var timer = new Promise(function (res) { setTimeout(res, 10000); });
        var get = fetch(window.theme.routes.cart_url + '?section_id=api-cart-items', { __mzRecoveryRows: true })
          .then(function (r) { return r.ok ? r.text() : null; })
          .then(function (text) {
            if (text === null || (window.__mzCartGen || 0) !== gen || window.__mzCartInflight > 0 || window.__mzRowsInflight > 0) return;
            var d = document.createElement('div');
            d.innerHTML = text;
            var c = d.querySelector('[data-api-content]');
            if (c && (window.__mzCartGen || 0) === gen && !(window.__mzCartInflight > 0) && !(window.__mzRowsInflight > 0)) self.build(c);
          })
          .catch(function () { /* the next step shows the cart's busy text */ });
        return Promise.race([get, timer]);
      });
    }
    function mzShowCartBusy(self) {
      try {
        var text = window.__mzCartBusyText;
        if (!text) return;
        if (self.cartErrorHolder && typeof self.updateErrorText === 'function' && typeof self.toggleErrorMessage === 'function') {
          var heading = self.cartErrorHolder.querySelector('.cart__errors__heading');   // the holder's own heading is Broadcast's stock limit sentence, wrong for this message: hidden until the holder is closed
          if (heading) {
            heading.hidden = true;
            var closeBtn = self.cartErrorHolder.querySelector('[data-cart-error-close]');
            if (closeBtn) closeBtn.addEventListener('click', function () { heading.hidden = false; }, { once: true });
          }
          self.cartUpdateFailed = true;
          self.updateErrorText(text);
          self.toggleErrorMessage();
        }
      } catch (e) { /* the controls are back, that is what matters */ }
    }
    function mzSettleControls(self, tries) {
      var gen = window.__mzCartGen || 0;
      if (!self.cart.classList.contains('is-loading')) {
        return mzQuiet().then(function (quiet) {
          if (!quiet || (window.__mzCartGen || 0) !== gen || window.__mzCartInflight > 0 || window.__mzRowsInflight > 0) return false;
          self.enableCartButtons();
          return true;
        });
      }
      if (tries >= 2) {
        return mzQuiet().then(function (quiet) {   // a cart change or another rows refresh still open owns the controls
          if (!quiet || (window.__mzCartGen || 0) !== gen || window.__mzCartInflight > 0 || window.__mzRowsInflight > 0) return false;
          self.cart.classList.remove('is-loading');
          self.enableCartButtons();
          mzShowCartBusy(self);
          return false;
        });
      }
      var wait = tries === 1 ? mzRecoveryRows(self, gen) : Promise.race([
        window.__mzNextCartRows ? window.__mzNextCartRows() : Promise.resolve(),
        new Promise(function (res) { setTimeout(res, 10000); })
      ]);
      return wait.then(function () {
        if ((window.__mzCartGen || 0) !== gen) return false;   // a newer cart action started: it owns the controls, nothing is done here
        return mzSettleControls(self, tries + 1);
      });
    }
    proto.updateCartDiscounts = function (codes) {
      var self = this;
      // WO-735 (Codex re-review): one discount operation at a time, so an older failed removal can never restore codes a newer one cleared
      if (self.__mzDiscountBusy) return Promise.resolve(false);
      self.__mzDiscountBusy = true;
      var last = String(codes).split(',').filter(function (c) { return c; }).pop();
      last = last && last.trim();
      this.disableCartButtons();
      this.discountErrorMessage.classList.add('hidden');
      var request = fetch(window.theme.routes.cart_update_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ discount: codes })
      });
      var gen = window.__mzCartGen || 0;
      return request.then(function (r) {
        if (!r.ok) throw new Error('HTTP error! status: ' + r.status);
        return r.text();
      }).then(function (text) {
        if ((window.__mzCartGen || 0) !== gen) return false;
        var data;
        try { data = JSON.parse(text); } catch (e) { throw new Error('Invalid JSON response from server.'); }
        if (last) {
          var ok = data.discount_codes && Array.isArray(data.discount_codes) && data.discount_codes.some(function (d) { return String(d.code).toLowerCase() === last.toLowerCase() && d.applicable; });
          if (ok) { self.discountError = false; } else {
            self.discountError = true;
            self.existingDiscountCodes = self.existingDiscountCodes.filter(function (c) { return String(c).toLowerCase() !== last.toLowerCase(); });
          }
        } else { self.discountError = false; }
        // WO-735 (replaces the WO-730 P1.5 early enable): the controls stay disabled while the rows refresh, so no quantity change can be made against rows that are about to be replaced;
        // v2: they ALWAYS come back once the refresh is over (answered and rebuilt, answered without a rebuild, failed, or 10 s)
        var rows = Promise.race([
          window.__mzNextCartRows ? window.__mzNextCartRows() : Promise.resolve(),
          new Promise(function (res) { setTimeout(res, 10000); })
        ]);
        self.getCart();
        // WO-735 v5: back on fresh rows only (see mzSettleControls); WO-735 v2 the wait is over once the refresh is done, skipped, failed or 10 s passed
        return rows.then(function () {
          if ((window.__mzCartGen || 0) !== gen) return false;
          return mzSettleControls(self, 0).then(function () { return true; });
        });
      }).catch(function (err) {
        console.log(err);
        if ((window.__mzCartGen || 0) !== gen) return false;
        return mzSettleControls(self, 0).then(function () { return false; });   // WO-735 v5: P1-B, never unlock over a newer action; WO-711 finding 17 the controls come back on any failure; WO-730 P2.2 the caller still gets false
      }).then(function (result) {
        self.__mzDiscountBusy = false;
        return result;
      });
    };
    // theme.js upgrades the initial cart before this deferred file runs and binds updateCart on the instance.
    document.querySelectorAll('cart-items').forEach(function (cart) {
      if (cart instanceof C && Object.prototype.hasOwnProperty.call(cart, 'updateCart')) cart.updateCart = proto.updateCart.bind(cart);
      if (cart instanceof C && Object.prototype.hasOwnProperty.call(cart, 'addToCart')) cart.addToCart = proto.addToCart.bind(cart);
      if (cart instanceof C && cart.cart) cart.cartCount = proto.getCartItemCount.call(cart);
    });
    bindKeyedQuantities();
    // Broadcast also captures an anonymous bound getCart listener during connection. Route its refresh event through the guarded method instead.
    document.addEventListener('theme:cart:refresh', function (event) {
      var carts = Array.prototype.filter.call(document.querySelectorAll('cart-items'), function (cart) { return cart instanceof C; });
      if (!carts.length) return;
      event.stopImmediatePropagation();
      carts.forEach(function (cart) { proto.getCart.call(cart); });
    }, true);
  }
  function patchCartDrawer(C) {
    var proto = C.prototype;
    if (proto.__mzFocusPatched) return;
    proto.__mzFocusPatched = true;
    proto.observeAdditionalCheckoutButtons = function () {
      var self = this;
      var target = this.querySelector('.additional-checkout-buttons');
      if (this.__mzWalletObserver) { this.__mzWalletObserver.disconnect(); this.__mzWalletObserver = null; }
      if (!target) return;
      var obs = new MutationObserver(function () {
        obs.disconnect();
        if (self.__mzWalletObserver === obs) self.__mzWalletObserver = null;
        if (!self.classList.contains('is-open')) return;   // WO-711 finding 19: the drawer closed meanwhile
        self.a11y.trapFocus(self, { elementToFocus: self.querySelector('[data-cart-drawer-close]') });
      });
      this.__mzWalletObserver = obs;
      obs.observe(target, { subtree: true, childList: true });
    };
    // the drawer binds closeCartDrawer in its constructor, so the wallet observer is released from the close event the drawer dispatches
    document.addEventListener('theme:cart-drawer:close', function () {
      document.querySelectorAll('cart-drawer').forEach(function (d) { if (d.__mzWalletObserver) { d.__mzWalletObserver.disconnect(); d.__mzWalletObserver = null; } });
    });
  }
  // trapFocus is shared: a call for a cart drawer that is not open is a late callback (the animation end after a quick close) and is skipped
  function patchA11y() {
    var a11y = window.theme && window.theme.a11y;
    if (!a11y || a11y.__mzTrapPatched || typeof a11y.trapFocus !== 'function') return;
    a11y.__mzTrapPatched = true;
    var trap = a11y.trapFocus;
    a11y.trapFocus = function (el) {
      if (el && el.tagName === 'CART-DRAWER' && !el.classList.contains('is-open')) return;
      return trap.apply(this, arguments);
    };
  }
  /* WO-719 BUY-03 (Codex): quick add's modal fetch (renderModal in assets/theme.js, Broadcast core, not edited) had no catch, so a failed fetch left the quick add button
     disabled and in its loading state for good. The override runs the same request and, on any failure, gives the button back and removes the loading class of the modal
     holder; a message from the server's error (error.description, a string) goes to the button's own error line, nothing else is written. */
  function patchQuickAdd(C) {
    var proto = C.prototype;
    if (proto.__mzQuickAddPatched) return;
    proto.__mzQuickAddPatched = true;
    proto.renderModal = function () {
      var self = this;
      return window.fetch(window.theme.routes.root + 'products/' + this.handle + '?section_id=api-product-upsell')
        .then(function (r) { return self.upsellErrorsHandler(r); })
        .then(function (r) { return r.text(); })
        .then(function (t) { self.modalCreate(t); })
        .catch(function (err) {
          if (self.modalButton) { self.modalButton.classList.remove('is-loading'); self.modalButton.disabled = false; }
          if (self.currentModal) self.currentModal.classList.remove('is-loading');
          var holder = self.quickAddHolder;
          var line = holder && holder.querySelector('[data-message-error]');
          if (line && err && typeof err.description === 'string' && err.description) line.innerText = err.description;
        });
    };
  }
  patchA11y();
  if (window.customElements) {
    customElements.whenDefined('quick-add-product').then(function () { patchQuickAdd(customElements.get('quick-add-product')); });
    customElements.whenDefined('cart-items').then(function () { patchCartItems(customElements.get('cart-items')); });
    customElements.whenDefined('cart-drawer').then(function () { patchCartDrawer(customElements.get('cart-drawer')); patchA11y(); });
  }
})();
